import { Star } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useState } from "react";

/** Small star-picker (1-5) used by the rating form. */
function StarPicker({ value, onChange, testid }) {
  return (
    <div className="flex items-center gap-0.5" data-testid={testid}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          onTouchEnd={(e) => { e.preventDefault(); onChange(n); }}
          aria-label={`Rate ${n} out of 5`}
          className="inline-flex h-11 w-11 items-center justify-center rounded-md transition active:scale-95 hover:bg-amber-50 sm:h-10 sm:w-10"
          data-testid={`${testid}-${n}`}
        >
          <Star
            className={`h-6 w-6 sm:h-5 sm:w-5 ${
              n <= value
                ? "fill-amber-400 text-amber-400"
                : "text-neutral-300"
            }`}
          />
        </button>
      ))}
      <span className="ml-2 text-xs text-neutral-500 tabular-nums">
        {value || "—"}/5
      </span>
    </div>
  );
}

// -------------------------------------------------------------- Text Class --
export function TextClassificationForm({ payload, value, onChange, readOnly }) {
  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-5">
          <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-neutral-500">
            Snippet
          </p>
          <p className="text-lg leading-relaxed text-neutral-900">
            {payload.text}
          </p>
        </CardContent>
      </Card>
      <div>
        <Label className="text-sm font-semibold">Choose a label</Label>
        <p className="mt-1 text-xs text-neutral-500">{payload.instruction}</p>
        <RadioGroup
          value={value.selected_label || ""}
          onValueChange={(v) => !readOnly && onChange({ ...value, selected_label: v })}
          className="mt-3 grid gap-2 sm:grid-cols-2"
        >
          {payload.labels.map((l) => (
            <label
              key={l}
              htmlFor={`lbl-${l}`}
              className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition ${
                value.selected_label === l
                  ? "border-amber-600 bg-amber-50"
                  : "border-neutral-200 hover:bg-neutral-50"
              }`}
            >
              <RadioGroupItem
                value={l}
                id={`lbl-${l}`}
                disabled={readOnly}
                data-testid={`label-${l}`}
              />
              <span className="text-sm font-medium">{l}</span>
            </label>
          ))}
        </RadioGroup>
      </div>
      <div>
        <Label className="text-sm font-semibold">Confidence (optional)</Label>
        <Select
          value={value.confidence || ""}
          onValueChange={(v) => !readOnly && onChange({ ...value, confidence: v })}
          disabled={readOnly}
        >
          <SelectTrigger className="mt-2 max-w-xs" data-testid="confidence-select">
            <SelectValue placeholder="How confident?" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Low">Low</SelectItem>
            <SelectItem value="Medium">Medium</SelectItem>
            <SelectItem value="High">High</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

// ------------------------------------------------------------- Image Label --
export function ImageLabelingForm({ payload, value, onChange, readOnly }) {
  const [draft, setDraft] = useState("");
  const tags = value.tags || [];
  const min = payload.min_tags || 3;
  const max = payload.max_tags || 10;

  const addTag = () => {
    const t = draft.trim();
    if (!t || tags.length >= max) return;
    if (tags.includes(t)) return;
    onChange({ ...value, tags: [...tags, t] });
    setDraft("");
  };
  const removeTag = (t) =>
    onChange({ ...value, tags: tags.filter((x) => x !== t) });

  return (
    <div className="space-y-4">
      <img
        src={payload.image_url}
        alt="task"
        className="mx-auto max-h-96 w-full rounded-lg border border-neutral-200 object-cover"
        data-testid="workspace-image"
      />
      <p className="text-xs text-neutral-500">{payload.instruction}</p>
      <div>
        <Label className="text-sm font-semibold">
          Tags{" "}
          <span
            className={
              tags.length < min ? "text-red-500" : "text-amber-700"
            }
          >
            ({tags.length}/{min} required)
          </span>
        </Label>
        <div className="mt-2 flex gap-2">
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
            placeholder="Type a tag and press Enter"
            disabled={readOnly}
            data-testid="tag-input"
          />
        </div>
        <div className="mt-3 flex flex-wrap gap-2" data-testid="tag-chips">
          {tags.map((t) => (
            <Badge
              key={t}
              onClick={() => !readOnly && removeTag(t)}
              className="cursor-pointer bg-amber-100 text-amber-900 hover:bg-amber-200"
            >
              {t} <span className="ml-1">×</span>
            </Badge>
          ))}
          {tags.length === 0 && (
            <p className="text-xs text-neutral-400">No tags yet</p>
          )}
        </div>
      </div>
      <div>
        <Label className="text-sm font-semibold">Primary subject (optional)</Label>
        <Input
          value={value.primary_subject || ""}
          onChange={(e) => onChange({ ...value, primary_subject: e.target.value })}
          disabled={readOnly}
          className="mt-2 max-w-md"
          placeholder="e.g. market, food, vehicle"
          data-testid="primary-subject-input"
        />
      </div>
    </div>
  );
}

// -------------------------------------------------------------- Translation --
export function TranslationForm({ payload, value, onChange, readOnly }) {
  const len = (value.translation || "").length;
  const min = payload.min_chars || 20;
  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-5">
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-neutral-500">
            <Badge variant="outline">{payload.source_language}</Badge>
            Source text
          </div>
          <p className="text-lg leading-relaxed text-neutral-900">
            {payload.source_text}
          </p>
        </CardContent>
      </Card>
      <div>
        <Label className="text-sm font-semibold">
          Translate to {payload.target_language}
        </Label>
        <p className="mt-1 text-xs text-neutral-500">{payload.instruction}</p>
        <Textarea
          value={value.translation || ""}
          onChange={(e) => onChange({ ...value, translation: e.target.value })}
          rows={5}
          disabled={readOnly}
          placeholder={`Write your ${payload.target_language} translation…`}
          className="mt-2"
          data-testid="translation-textarea"
        />
        <p className="mt-1 text-xs text-neutral-500">
          <span
            className={len < min ? "font-semibold text-red-500" : "text-amber-700"}
          >
            {len}
          </span>{" "}
          / {min} chars minimum
        </p>
      </div>
    </div>
  );
}

// ------------------------------------------------------- Audio Transcription --
export function AudioTranscriptionForm({ payload, value, onChange, readOnly }) {
  const [speed, setSpeed] = useState(1);
  const len = (value.transcription || "").length;
  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-5">
          <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-neutral-500">
            Audio clip
          </p>
          <audio
            controls
            src={payload.audio_url}
            className="w-full"
            data-testid="workspace-audio"
            onPlay={(e) => (e.currentTarget.playbackRate = speed)}
          />
          <div className="mt-3 flex items-center gap-2 text-xs text-neutral-500">
            <span>Playback speed:</span>
            {[0.75, 1, 1.25].map((s) => (
              <button
                key={s}
                onClick={() => {
                  setSpeed(s);
                  const el = document.querySelector('audio[data-testid="workspace-audio"]');
                  if (el) el.playbackRate = s;
                }}
                className={`rounded-md px-2 py-1 font-medium transition ${
                  speed === s
                    ? "bg-amber-700 text-white"
                    : "bg-neutral-100 hover:bg-neutral-200"
                }`}
              >
                {s}x
              </button>
            ))}
          </div>
        </CardContent>
      </Card>
      <div>
        <Label className="text-sm font-semibold">Transcription</Label>
        <p className="mt-1 text-xs text-neutral-500">{payload.instruction}</p>
        <Textarea
          value={value.transcription || ""}
          onChange={(e) => onChange({ ...value, transcription: e.target.value })}
          rows={6}
          disabled={readOnly}
          placeholder="Type what you hear…"
          className="mt-2"
          data-testid="transcription-textarea"
        />
        <p className="mt-1 text-xs text-neutral-500">{len} chars</p>
      </div>
    </div>
  );
}

// ------------------------------------------------------------- Prompt Rating --
export function PromptRatingForm({ payload, value, onChange, readOnly }) {
  const ratings = value.ratings || {};
  const setRating = (crit, n) => {
    if (readOnly) return;
    onChange({ ...value, ratings: { ...ratings, [crit]: n } });
  };
  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-2">
        <Card>
          <CardContent className="p-5">
            <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-neutral-500">
              Prompt
            </p>
            <p className="text-sm leading-relaxed text-neutral-900">
              {payload.prompt}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-neutral-500">
              AI Response
            </p>
            <p className="text-sm leading-relaxed text-neutral-900">
              {payload.response}
            </p>
          </CardContent>
        </Card>
      </div>
      <div className="space-y-3">
        {(payload.criteria || []).map((c) => (
          <div key={c} className="flex items-center justify-between rounded-lg border border-neutral-200 bg-white p-3">
            <span className="text-sm font-medium">{c}</span>
            <StarPicker
              value={ratings[c] || 0}
              onChange={(n) => setRating(c, n)}
              testid={`stars-${c.toLowerCase().replace(/\s+/g, "-")}`}
            />
          </div>
        ))}
        <div className="flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 p-3">
          <span className="text-sm font-semibold">Overall</span>
          <StarPicker
            value={value.overall || 0}
            onChange={(n) => !readOnly && onChange({ ...value, overall: n })}
            testid="stars-overall"
          />
        </div>
      </div>
      <div>
        <Label className="text-sm font-semibold">Feedback (optional)</Label>
        <Textarea
          value={value.feedback || ""}
          onChange={(e) => onChange({ ...value, feedback: e.target.value })}
          rows={3}
          disabled={readOnly}
          placeholder="What worked, what didn't?"
          className="mt-2"
          data-testid="feedback-textarea"
        />
      </div>
    </div>
  );
}

// ------------------------------------------------------------- Form router --
export function WorkspaceForm({ task_type, payload, value, onChange, readOnly }) {
  const props = { payload, value, onChange, readOnly };
  if (task_type === "text_classification") return <TextClassificationForm {...props} />;
  if (task_type === "image_labeling") return <ImageLabelingForm {...props} />;
  if (task_type === "translation") return <TranslationForm {...props} />;
  if (task_type === "audio_transcription") return <AudioTranscriptionForm {...props} />;
  if (task_type === "prompt_rating" || task_type === "voice_rating")
    return <PromptRatingForm {...props} />;
  return <p className="text-sm text-neutral-500">Unsupported task type.</p>;
}
